import produce from 'immer';

import {
  entitiesToTableParser,
  instrumentsToTableParser,
  artifactsParser,
  creditReviewToTableParser,
  creditReviewListToTableParser,
  esgImpactToTableParser,
  esgSentimentToTableParser,
  climateStressTestToTableParser,
} from 'shared/parsers';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { REQUEST_RATING_NEWS, REQUEST_RATING_NEWS_FINISHED } from 'store/newsFeed/newsFeedActions';
import {
  SEARCH_START,
  SEARCH_ENTITIES,
  SEARCH_ENTITIES_SUCCESS,
  SEARCH_ENTITIES_SWITCH_SCENARIO,
  SEARCH_ENTITIES_ERROR,
  SEARCH_INSTRUMENTS,
  SEARCH_INSTRUMENTS_SUCCESS,
  SEARCH_INSTRUMENTS_ERROR,
  SEARCH_RATING_NEWS,
  SEARCH_RATING_NEWS_SUCCESS,
  SEARCH_RATING_NEWS_ERROR,
  SEARCH_RESEARCH,
  SEARCH_RESEARCH_SUCCESS,
  SEARCH_RESEARCH_ERROR,
  SET_SEARCH_FILTERS,
} from './searchResultActions';

import { REQUEST_SIG_ENTITY_RATING_SUCCESS } from '../entity/entityActions';
import { changeScenario } from '../../shared/parsers/portfoliosToTable/climateStressTestToTableParser';

const entitiesByRatingType = Object.values(ratingTypes).reduce((acc, ratingType) => {
  acc[ratingType] = {
    isPending: false,
    data: [],
    total: 0,
  };

  return acc;
}, {});

const initialState = {
  isSearchStarted: false,
  isRatingRequestPending: false,
  ratingRequestError: false,
  entities: {
    isPending: false,
    error: undefined,
    byRatingType: entitiesByRatingType,
  },
  instruments: {
    isPending: false,
    data: [],
    total: 0,
  },
  research: {
    isPending: false,
    data: [],
    total: 0,
    shouldRefresh: false,
  },
  ratingNews: {
    isPending: false,
    data: [],
    total: 0,
    shouldRefresh: false,
  },
  creditReview: {
    isPending: false,
    data: [],
    total: 0,
  },
  searchFilters: {},
};

const parserByRatingType = {
  [ratingTypes.CREDIT_RATING]: entitiesToTableParser,
  [ratingTypes.CREDIT_REVIEW]: creditReviewListToTableParser,
  [ratingTypes.ESG_IMPACT]: esgImpactToTableParser,
  [ratingTypes.SENTIMENT]: esgSentimentToTableParser,
  [ratingTypes.CLIMATE_STRESS_TEST]: climateStressTestToTableParser,
};

export function searchResult(state = initialState, action) {
  const { payload, type } = action;

  switch (type) {
    case SEARCH_START: {
      return { ...state, isSearchStarted: true };
    }
    case SEARCH_ENTITIES: {
      const { ratingType } = payload;

      return produce(state, (draftState) => {
        // eslint-disable-next-line no-param-reassign
        draftState.entities.byRatingType[ratingType] = {
          ...initialState.entities.byRatingType[ratingType],
          isPending: true,
        };
        // eslint-disable-next-line no-param-reassign
        draftState.isSearchStarted = false;
      });
    }
    case SEARCH_ENTITIES_SUCCESS: {
      const { response, ratingType } = payload;

      return produce(state, (draftState) => {
        const entities = state.entities.byRatingType[ratingType];
        const parser = parserByRatingType[ratingType];
        // eslint-disable-next-line no-param-reassign
        draftState.entities.byRatingType[ratingType] = {
          ...entities,
          isPending: false,
          data: parser(ratingType === ratingTypes.CLIMATE_STRESS_TEST ? response : response.data),
          total: ratingType === ratingTypes.CLIMATE_STRESS_TEST ? response.length : response.total,
        };
      });
    }
    case SEARCH_ENTITIES_SWITCH_SCENARIO: {
      const { scenario } = payload;
      return produce(state, (draftState) => {
        const ratingType = ratingTypes.CLIMATE_STRESS_TEST;
        const entities = state.entities.byRatingType[ratingType];
        draftState.entities.byRatingType[ratingType] = {
          ...entities,
          isPending: false,
          data: changeScenario(entities.data, scenario),
        };
      });
    }
    case SEARCH_ENTITIES_ERROR: {
      const { ratingType, response } = payload;

      return produce(state, (draftState) => {
        // eslint-disable-next-line no-param-reassign
        draftState.entities.byRatingType[ratingType] = {
          ...initialState.entities.byRatingType[ratingType],
          isPending: false,
          error: response && response.error,
        };
      });
    }
    case SEARCH_INSTRUMENTS:
      return {
        ...state,
        instruments: { ...initialState.instruments, isPending: true },
        isSearchStarted: false,
      };
    case SEARCH_INSTRUMENTS_SUCCESS: {
      const { data, total } = payload;
      return {
        ...state,
        instruments: { data: instrumentsToTableParser(data), total, isPending: false },
      };
    }
    case SEARCH_INSTRUMENTS_ERROR:
      return { ...state, instruments: { ...initialState.instruments, isPending: false } };
    case SEARCH_RATING_NEWS:
      return {
        ...state,
        ratingNews: { ...initialState.ratingNews, isPending: true, shouldRefresh: false },
        isSearchStarted: false,
      };
    case SEARCH_RATING_NEWS_SUCCESS: {
      const { data, total } = payload;
      return { ...state, ratingNews: { data: artifactsParser(data), total, isPending: false } };
    }
    case SEARCH_RATING_NEWS_ERROR:
      return { ...state, ratingNews: { ...initialState.ratingNews, isPending: false } };
    case SEARCH_RESEARCH:
      return {
        ...state,
        research: { ...initialState.research, isPending: true, shouldRefresh: false },
        isSearchStarted: false,
      };
    case SEARCH_RESEARCH_SUCCESS: {
      const { data, total } = payload;
      return { ...state, research: { data: artifactsParser(data), total, isPending: false } };
    }
    case SEARCH_RESEARCH_ERROR:
      return { ...state, research: { ...initialState.research, isPending: false } };
    case REQUEST_RATING_NEWS:
      return { ...state, isRatingRequestPending: true, ratingRequestError: false };
    case REQUEST_RATING_NEWS_FINISHED:
      return {
        ...state,
        isRatingRequestPending: false,
        research: {
          ...initialState.research,
          shouldRefresh: !(payload && payload.error),
        },
        ratingNews: {
          ...initialState.ratingNews,
          shouldRefresh: !(payload && payload.error),
        },
        ratingRequestError: !!(payload && payload.error),
      };
    case REQUEST_SIG_ENTITY_RATING_SUCCESS: {
      const ratingType = ratingTypes.CREDIT_REVIEW;
      const updatedItems = payload;

      return produce(state, (draftState) => {
        // eslint-disable-next-line no-param-reassign
        draftState.entities.byRatingType[ratingType].data = state.entities.byRatingType[
          ratingType
        ].data.map((item) => {
          const easyNumber = item[0];
          const updatedItem = updatedItems.find((company) => company.easyNumber === easyNumber);

          if (updatedItem) return creditReviewToTableParser(updatedItem);

          return item;
        });
      });
    }
    case SET_SEARCH_FILTERS:
      return { ...state, searchFilters: payload };
    default:
      return state;
  }
}
